import { useEffect, useRef, useState, type Dispatch, type SetStateAction } from 'react';
import { IconFileText, IconUpload, IconX } from '@/components/Icons';
import { ACCEPT_ATTRIBUTE, formatFileSize, isImage, MAX_FILES, prepareFiles } from '@/lib/files';

export interface PickedFile {
  id: string;
  file: File;
  /** URL local da miniatura — só imagens. */
  previewUrl: string | null;
}

interface FilePickerProps {
  files: PickedFile[];
  onChange: Dispatch<SetStateAction<PickedFile[]>>;
  onBusyChange: (busy: boolean) => void;
  onProblem: (message: string) => void;
}

let sequence = 0;

/**
 * Onde o comprovante entra: arrastar e soltar, colar um print com Ctrl+V ou
 * escolher do computador ou do celular. Os prints são comprimidos aqui mesmo,
 * antes do envio.
 */
export function FilePicker({ files, onChange, onBusyChange, onProblem }: FilePickerProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const previews = useRef(new Set<string>());
  const [over, setOver] = useState(false);
  const [busy, setBusy] = useState(false);
  // A trava de verdade: muda na hora, não no próximo render — dois lotes quase
  // juntos (Ctrl+V duplo, soltar e colar) nunca passam os dois.
  const working = useRef(false);
  const room = MAX_FILES - files.length;

  async function add(list: File[]) {
    // Um lote por vez: assim o limite de arquivos nunca estoura com dois lotes simultâneos.
    if (working.current || list.length === 0) return;
    working.current = true;
    setBusy(true);
    onBusyChange(true);
    try {
      const { files: prepared, problems } = await prepareFiles(list, room);
      problems.forEach(onProblem);
      const picked = prepared.map((file) => {
        const previewUrl = isImage(file.type) ? URL.createObjectURL(file) : null;
        if (previewUrl) previews.current.add(previewUrl);
        sequence += 1;
        return { id: `arquivo-${sequence}`, file, previewUrl };
      });
      // A conta final é com a lista de agora, não com a do render em que o lote começou.
      onChange((current) => [...current, ...picked].slice(0, MAX_FILES));
    } finally {
      working.current = false;
      setBusy(false);
      onBusyChange(false);
    }
  }

  function remove(picked: PickedFile) {
    if (picked.previewUrl) {
      URL.revokeObjectURL(picked.previewUrl);
      previews.current.delete(picked.previewUrl);
    }
    onChange((current) => current.filter((item) => item.id !== picked.id));
  }

  // Colar um print em qualquer ponto do formulário. Texto colado num campo segue normal.
  const addRef = useRef(add);
  useEffect(() => {
    addRef.current = add;
  });
  useEffect(() => {
    const onPaste = (event: ClipboardEvent) => {
      const pasted = Array.from(event.clipboardData?.files ?? []);
      if (pasted.length === 0) return;
      event.preventDefault();
      void addRef.current(pasted);
    };
    window.addEventListener('paste', onPaste);
    return () => window.removeEventListener('paste', onPaste);
  }, []);

  // Ao fechar, as miniaturas que sobraram são liberadas.
  useEffect(() => {
    const urls = previews.current;
    return () => urls.forEach((url) => URL.revokeObjectURL(url));
  }, []);

  const openChooser = () => inputRef.current?.click();

  return (
    <div className="field">
      <span className="field-label">Comprovante *</span>

      {room > 0 ? (
        <div
          className={`dropzone${over ? ' is-over' : ''}${busy ? ' is-busy' : ''}`}
          role="button"
          tabIndex={0}
          aria-label="Anexar o print ou o PDF do comprovante"
          aria-busy={busy}
          onClick={openChooser}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              openChooser();
            }
          }}
          onDragOver={(event) => {
            event.preventDefault();
            setOver(true);
          }}
          onDragLeave={() => setOver(false)}
          onDrop={(event) => {
            event.preventDefault();
            setOver(false);
            void add(Array.from(event.dataTransfer.files));
          }}
        >
          {busy ? <span className="spinner" /> : <IconUpload size={20} />}
          <span className="dropzone-title">{busy ? 'Preparando os arquivos…' : 'Solte aqui o print ou o PDF'}</span>
          <span className="dropzone-paste">
            ou cole com <kbd>Ctrl</kbd> + <kbd>V</kbd> — ou clique para escolher
          </span>
          <span className="dropzone-touch">Toque para escolher uma foto ou um PDF</span>
        </div>
      ) : null}

      <input
        ref={inputRef}
        type="file"
        multiple
        accept={ACCEPT_ATTRIBUTE}
        hidden
        onChange={(event) => {
          const chosen = Array.from(event.currentTarget.files ?? []);
          event.currentTarget.value = '';
          void add(chosen);
        }}
      />

      <span className="field-hint">
        JPEG, PNG, WEBP ou PDF · até 5 MB cada · {files.length} de {MAX_FILES}
      </span>

      {files.length > 0 ? (
        <div className="file-grid" style={{ marginTop: 4 }}>
          {files.map((picked) => (
            <div key={picked.id} className="file-tile">
              <div className="file-thumb">
                {picked.previewUrl ? <img src={picked.previewUrl} alt="" /> : <IconFileText size={26} />}
              </div>
              <div className="file-meta">
                <span className="truncate" title={picked.file.name}>{picked.file.name}</span>
                <span className="muted num">{formatFileSize(picked.file.size)}</span>
              </div>
              <button
                type="button"
                className="icon-btn file-remove"
                aria-label={`Tirar ${picked.file.name}`}
                title="Tirar este arquivo"
                onClick={() => remove(picked)}
              >
                <IconX />
              </button>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}
